import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import type {
	RestrictedUserPrincipal,
	StagedPrincipalsAddMode,
	UserPrincipal,
} from '../commonTypes.tsx';
import type { MasterState, MasterStateSetter } from '../../controller/masterState.ts';
// eslint-disable-next-line import/no-cycle
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../controller/shareAndRestrictDialogConfig.ts';
import type { ControllerExtras } from '../../controller/Controller.tsx';
import { isUserPendingAccess } from '../../controller/mutations/restrictionsTransformer.ts';

import type {
	BlockedAccessRestrictionsQuery$data,
	BlockedAccessRestrictionsQuery$variables,
	BlockedAccessSubjectInput,
} from './queries/__generated__/BlockedAccessRestrictionsQuery.graphql.ts';
import type { BlockedAccessRestrictionsQuery_blockedAccessRestrictions_blockedAccessAssignableSpaceRolesIntersection } from './queries/__types__/BlockedAccessRestrictionsQuery.ts';
import BlockedAccessRestrictionsQuery from './queries/__generated__/BlockedAccessRestrictionsQuery.graphql.ts';
import UsersContentAccessQuery from './queries/__generated__/UsersContentAccessQuery.graphql.ts';
import type {
	UsersContentAccessQuery$variables,
	UsersContentAccessQuery$data,
} from './queries/__generated__/UsersContentAccessQuery.graphql.ts';

const blockedAccessUsersLimit = 10;
const blockedAccessLevelsLimit = 10;

export type ValidateRestrictedPrincipalsProps = {
	contentId: string;
	cloudId: string;
	edition: ConfluenceEdition;
	numberOfAncestors: number;
	addMode: StagedPrincipalsAddMode;
	stagedUserPrincipals: UserPrincipal[];
	setMasterState: MasterStateSetter;
	fireAnalyticEvent: ControllerExtras['fireAnalyticEvent'];
};

export const validateRestrictedPrincipals = async ({
	contentId,
	cloudId,
	edition,
	numberOfAncestors,
	addMode,
	stagedUserPrincipals,
	setMasterState,
	fireAnalyticEvent,
}: ValidateRestrictedPrincipalsProps): Promise<{
	showWarnRestrictionsDialog: boolean;
	showResolveRestrictionsDialog: boolean;
	restrictedUserPrincipals?: RestrictedUserPrincipal[];
	assignableSpaceRolesIntersection?: ReadonlyArray<
		| BlockedAccessRestrictionsQuery_blockedAccessRestrictions_blockedAccessAssignableSpaceRolesIntersection
		| null
		| undefined
	>;
}> => {
	//The validation  is skipped if free edition
	if (edition == ConfluenceEdition.FREE) {
		return {
			showWarnRestrictionsDialog: false,
			showResolveRestrictionsDialog: false,
		};
	}

	const activeStagedUserPrincipals = stagedUserPrincipals.filter(
		(user) => !isUserPendingAccess({ type: 'user', ...user }),
	);
	//The validation  is skipped if staged user count is 0
	if (activeStagedUserPrincipals.length == 0) {
		return {
			showWarnRestrictionsDialog: false,
			showResolveRestrictionsDialog: false,
		};
	}
	// if staged user count is greater than 10,
	// or content is more than 10 levels deep
	if (
		activeStagedUserPrincipals.length > blockedAccessUsersLimit ||
		numberOfAncestors > blockedAccessLevelsLimit
	) {
		fireAnalyticEvent({
			eventType: 'sendOperationalEvent',
			action: 'BlockedAccessCheckLimitExceeded',
			actionSubject: 'BlockedAccessCheck',
			source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
			attributes: {
				NewUsersCount: activeStagedUserPrincipals.length,
				numberOfAncestors,
			},
		});

		const accountIds = activeStagedUserPrincipals.map((user) => {
			return user.id;
		});

		const { data: usersContentAccessData, error: usersContentAccessError } = await queryInterop<
			UsersContentAccessQuery$data,
			UsersContentAccessQuery$variables
		>({
			query: UsersContentAccessQuery,
			variables: {
				accessType: addMode == 'as-editor' ? 'EDIT' : 'VIEW',
				accountIds,
				cloudId,
				contentId,
			},
			fetchPolicy: 'network-only',
		});

		if (usersContentAccessError) {
			fireAnalyticEvent({
				eventType: 'sendOperationalEvent',
				action: 'errored',
				actionSubject: 'UsersContentAccessQuery',
				actionSubjectId: 'validateRestrictedPrincipals',
				attributes: {
					message: usersContentAccessError.message,
				},
			});
			return {
				showWarnRestrictionsDialog: false,
				showResolveRestrictionsDialog: false,
			};
		}
		const canNotAccessList = usersContentAccessData?.confluence_userContentAccess?.cannotAccessList;

		if (canNotAccessList != null && canNotAccessList.length > 0) {
			const restrictedUserPrincipalsList = canNotAccessList.map((userId) => {
				const restrictedPrincipal: RestrictedUserPrincipal = {
					userPrincipal: activeStagedUserPrincipals.find((val) => val.id === userId),
					restrictedResources: null,
				};
				return restrictedPrincipal;
			});
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					restrictedUserPrincipals: restrictedUserPrincipalsList,
				}),
			);
			return {
				showWarnRestrictionsDialog: true,
				showResolveRestrictionsDialog: false,
			};
		}
	} else {
		const blockedAccessSubjects = activeStagedUserPrincipals.map((user) => {
			const blockedAccessSubject: BlockedAccessSubjectInput = {
				subjectId: user.id,
				subjectType: 'USER',
			};
			return blockedAccessSubject;
		});

		const { data, error } = await queryInterop<
			BlockedAccessRestrictionsQuery$data,
			BlockedAccessRestrictionsQuery$variables
		>({
			query: BlockedAccessRestrictionsQuery,
			variables: {
				// The GraphQL query expects a number, but contentId is a string. For the relay migration we will type it as any to not break existing functionality,
				// but this should be fixed in the future and keep the types consistent.
				contentId: contentId as any,
				accessType: addMode == 'as-editor' ? 'EDIT' : 'VIEW',
				subjects: blockedAccessSubjects,
			},
			fetchPolicy: 'network-only',
		});

		if (error) {
			fireAnalyticEvent({
				eventType: 'sendOperationalEvent',
				action: 'errored',
				actionSubject: 'BlockedAccessRestrictionsQuery',
				actionSubjectId: 'validateRestrictedPrincipals',
				attributes: {
					message: error?.message,
				},
			});
		}

		const blockedAccessRestrictions = data?.blockedAccessRestrictions;
		const blockedAccessRestrictionSummary =
			blockedAccessRestrictions?.blockedAccessRestrictionSummary;
		if (
			blockedAccessRestrictions &&
			blockedAccessRestrictionSummary &&
			blockedAccessRestrictionSummary.length > 0
		) {
			const restrictedUserPrincipalsList = blockedAccessRestrictionSummary.map((restrictedUser) => {
				const restrictedPrincipal: RestrictedUserPrincipal = {
					userPrincipal: activeStagedUserPrincipals.find(
						(val) => val.id === restrictedUser?.subject?.subjectId,
					),
					restrictedResources: restrictedUser?.restrictedResources,
				};
				return restrictedPrincipal;
			});
			const assignableSpaceRolesIntersection =
				blockedAccessRestrictions.blockedAccessAssignableSpaceRolesIntersection;
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					restrictedUserPrincipals: restrictedUserPrincipalsList,
					assignableSpaceRolesIntersection,
				}),
			);

			//current user is not empowered to fix at least one of the selected user's restrictions
			if (!blockedAccessRestrictions.canFixRestrictionsForAllSubjects) {
				return {
					showWarnRestrictionsDialog: true,
					showResolveRestrictionsDialog: false,
				};
			}

			return {
				showWarnRestrictionsDialog: false,
				showResolveRestrictionsDialog: true,
				// Expose this extra info for Realtime requests to consume
				// In USD these get set directly on the masterState
				// But realtime requests don't have access to masterState
				restrictedUserPrincipals: restrictedUserPrincipalsList,
				assignableSpaceRolesIntersection,
			};
		}
	}
	// all added principles can access or blocked access was skipped
	return {
		showWarnRestrictionsDialog: false,
		showResolveRestrictionsDialog: false,
	};
};
